import {
  emitter,
  experimentDebugger,
  useExperiment,
} from "@marvelapp/react-ab-test";

export interface UseExperiment {
  experimentName: string;
  activeVariant: string;
  emitWin: (experimentName: string) => void;
  selectVariant: (variants: { [variantName: string]: unknown }) => unknown;
}

export enum Experiments {
  socialSignon = "Social signon",
  houseLookup = "company lookup",
  fundingCalculatorDriver = "funding calculator driver",
  todayOffers = "today personalised offers",
}

type ValuesOfExperiments = `${Experiments}`;

export enum SocialSignonExperimentVariants {
  none = "none",
  ssoTop = "sso on top",
  ssoBottom = "sso on bottom",
}

type ValuesOfSocialSignonExperimentVariants =
  `${SocialSignonExperimentVariants}`;

export enum CompanyVariants {
  houseLookup = "companies house lookup",
  experianLookup = "experian lookup",
}

type ValuesOfCompanyVariants = `${CompanyVariants}`;

export enum FundingCalculatorDriverVariants {
  boostCashflow = "boost cashflow",
  goodCredit = "good credit",
}

type ValuesOfFundingCalculatorDriverVariants =
  `${FundingCalculatorDriverVariants}`;

export enum PersonalizedOffersVariants {
  none = "none",
  fundingCalculatorDriver = "funding calculator driver",
}

type ValuesOfPersonalizedOffersVariants = `${PersonalizedOffersVariants}`;

interface Variant<T> {
  variant: T;
  weight: number;
}

export const Variants: Record<
  Experiments,
  Variant<
    | ValuesOfSocialSignonExperimentVariants
    | ValuesOfCompanyVariants
    | ValuesOfFundingCalculatorDriverVariants
    | ValuesOfPersonalizedOffersVariants
  >[]
> = Object.freeze({
  [Experiments.socialSignon]: [
    {
      variant: SocialSignonExperimentVariants.none,
      weight: 80,
    },
    {
      variant: SocialSignonExperimentVariants.ssoTop,
      weight: 10,
    },
    {
      variant: SocialSignonExperimentVariants.ssoBottom,
      weight: 10,
    },
  ],
  [Experiments.houseLookup]: [
    {
      variant: CompanyVariants.houseLookup,
      weight: 0,
    },
    {
      variant: CompanyVariants.experianLookup,
      weight: 100,
    },
  ],
  [Experiments.fundingCalculatorDriver]: [
    {
      variant: FundingCalculatorDriverVariants.boostCashflow,
      weight: 50,
    },
    {
      variant: FundingCalculatorDriverVariants.goodCredit,
      weight: 50,
    },
  ],
  [Experiments.todayOffers]: [
    {
      variant: PersonalizedOffersVariants.none,
      weight: 50,
    },
    {
      variant: PersonalizedOffersVariants.fundingCalculatorDriver,
      weight: 50,
    },
  ],
});

const ABTesting = {
  init: (debug = false) => {
    Object.entries(Variants).forEach(([experiment, variants]) => {
      const variantNames = variants.map((variant) => variant.variant);
      const variantWeights = variants.map((variant) => variant.weight);
      emitter.defineVariants(experiment, variantNames, variantWeights);
    });
    if (debug) {
      experimentDebugger.setDebuggerAvailable(true);
      experimentDebugger.enable();
    }
  },
  useExperiment: (experiment: ValuesOfExperiments): UseExperiment => {
    const { experimentName, activeVariant, emitWin, selectVariant } =
      useExperiment(experiment);
    const defaultVariant = Variants?.[experiment]?.[0].variant;
    let variantExists = false;
    try {
      variantExists = Variants[experiment].some(
        (experimentVariant) => experimentVariant.variant === activeVariant
      );
    } catch (e) {
      variantExists = false;
    }

    return {
      experimentName,
      activeVariant: variantExists ? activeVariant : defaultVariant,
      emitWin,
      selectVariant: variantExists
        ? selectVariant
        : (variants: { [variantName: string]: unknown }) => {
            return variants[defaultVariant];
          },
    };
  },
};

export default ABTesting;
